import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../utils/api";
import Swal from "sweetalert2";
import { pushAddToCart } from "../utils/gtmAddToCart"; // adjust path if needed

/**
 * Drives the Part Details screen.
 * Keeps ALL logic / effects here; the view stays dumb.
 */
export default function usePartDetailsController({
  part,
  brandName,
  modelName,
  yearValue,
  categoryValue,
  onBuyNow,
}) {
  const [mainImage, setMainImage] = useState(null);
  const [qty, setQty] = useState(1);

  // cart presence
  const [cartItemId, setCartItemId] = useState(null);
  const [checkingCart, setCheckingCart] = useState(false);

  // guest modal
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [guestPhone, setGuestPhone] = useState(localStorage.getItem("guestPhone") || "");
  const [guestLoading, setGuestLoading] = useState(false);
  const [guestError, setGuestError] = useState("");

  // share pop (kept for future use)
  const [showShareBox, setShowShareBox] = useState(false);
  const shareRef = useRef(null);

  /* ---------- effects ---------- */

  // pick first image by default
  useEffect(() => {
    if (Array.isArray(part?.images) && part.images.length > 0) {
      setMainImage(part.images[0]);
    } else {
      setMainImage(null);
    }
  }, [part?.images]);

  // close share pop when clicking outside
  useEffect(() => {
    const onOutside = (e) => {
      if (!shareRef.current) return;
      if (!shareRef.current.contains(e.target)) setShowShareBox(false);
    };
    if (showShareBox) {
      document.addEventListener("mousedown", onOutside);
      document.addEventListener("touchstart", onOutside);
    }
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("touchstart", onOutside);
    };
  }, [showShareBox]);

  // if authed, check if this part already exists in pending cart
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token || !part?.id) {
      setCartItemId(null);
      return;
    }
    let cancelled = false;

    (async () => {
      try {
        setCheckingCart(true);
        const res = await axios.get(`${API_BASE_URL}/carts/pending`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const arr = Array.isArray(res.data) ? res.data : [];
        const cart = arr[0];
        const found = cart?.items?.find((it) => Number(it.part_id) === Number(part.id));
        if (!cancelled) setCartItemId(found ? found.id : null);
      } catch {
        if (!cancelled) setCartItemId(null);
      } finally {
        if (!cancelled) setCheckingCart(false);
      }
    })();

    return () => { cancelled = true; };
  }, [part?.id]);

  /* ---------- actions ---------- */

  const addToCartAuth = async () => {
    const token = localStorage.getItem("token");
    try {
      const payload = {
        items: [{ part_id: part.id, price: part.price ?? 0 }],
      };
      const res = await axios.post(`${API_BASE_URL}/add/carts`, payload, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });

      const added =
        res.data?.cart?.items?.find((it) => Number(it.part_id) === Number(part.id)) ?? null;

      if (added?.id) {
        setCartItemId(added.id);

        // ✅ Track only on confirmed success
        pushAddToCart({
          item_id: part.id,                          // part id
          item_name: part.name ?? item.label ?? "",  // part name
          price: part.price ?? 0,                    // part price
          quantity: 1                                // one item per click
        });

        await Swal.fire({
          icon: "success",
          title: "تمت الإضافة",
          text: "✅ تمت إضافة القطعة إلى السلة",
          confirmButtonText: "موافق",
          confirmButtonColor: "#CAEA3E",
          iconColor: "#CAEA3E",
          customClass: {
            popup: "swal-dark-popup",
            cancelButton: "swal-cancel-btn",
          },
        });
        return;
      }

      // fallback re-check
      try {
        const again = await axios.get(`${API_BASE_URL}/carts/pending`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const arr = Array.isArray(again.data) ? again.data : [];
        const cart = arr[0];
        const found = cart?.items?.find((it) => Number(it.part_id) === Number(part.id));
        setCartItemId(found ? found.id : null);

        // ✅ Track only if we positively located the item in the pending cart
        if (found?.id) {
          pushAddToCart({
            item_id: item.id,
            item_name: item.name ?? item.label ?? "",
            price: item.price ?? 0,
            quantity: 1
          });
        }
      } catch { }

      await Swal.fire({
        icon: "warning",
        title: "تنبيه",
        text: "⚠️ أضفنا القطعة، لكن لم نتمكن من قراءة معرّفها من الاستجابة.",
        confirmButtonText: "موافق",
        confirmButtonColor: "#CAEA3E",
        iconColor: "#CAEA3E",
        customClass: {
          popup: "swal-dark-popup",
          cancelButton: "swal-cancel-btn",
        },
      });
    } catch (err) {
      console.log(err);
      
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;

        if (status === 401) {
          return Swal.fire({
            icon: "warning",
            title: "انتهت الجلسة",
            text: "⚠️ انتهت الجلسة. سجّل الدخول.",
            confirmButtonText: "موافق",
            confirmButtonColor: "#CAEA3E",
            iconColor: "#CAEA3E",
            customClass: {
              popup: "swal-dark-popup",
              cancelButton: "swal-cancel-btn",
            },
          });
        }

        if (status === 422) {
          const first = Object.values(err.response?.data?.errors || {}).flat()?.[0];
          return Swal.fire({
            icon: "error",
            title: "خطأ في الإدخال",
            text: `⚠️ ${first || "تحقق من البيانات."}`,
            confirmButtonText: "موافق",
            confirmButtonColor: "#CAEA3E",
            iconColor: "#CAEA3E",
            customClass: {
              popup: "swal-dark-popup",
              cancelButton: "swal-cancel-btn",
            },
          });
        }
      }

      await Swal.fire({
        icon: "error",
        title: "خطأ",
        text: "⚠️ حدث خطأ أثناء الإضافة إلى السلة.",
        confirmButtonText: "موافق",
        confirmButtonColor: "#CAEA3E",
        iconColor: "#CAEA3E",
        customClass: {
          popup: "swal-dark-popup",
          cancelButton: "swal-cancel-btn",
        },
      });
    }
  };


  const handleRemoveFromCart = async () => {
    const token = localStorage.getItem("token");
    if (!token) return alert("يرجى تسجيل الدخول.");
    if (!cartItemId) return alert("لا يوجد عنصر لحذفه.");

    try {
      await axios.delete(`${API_BASE_URL}/cart-items/${cartItemId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCartItemId(null);
      alert("🗑️ تم حذف القطعة من السلة.");
    } catch {
      alert("⚠️ لم نتمكن من حذف القطعة من السلة.");
    }
  };

  // guest flow
  const openGuestModal = () => { setGuestError(""); setShowGuestModal(true); };
  const closeGuestModal = () => { if (!guestLoading) setShowGuestModal(false); };

  const confirmGuestAdd = async () => {
    const normalized = guestPhone.replace(/\D/g, "");
    if (!normalized || normalized.length < 8) {
      setGuestError("رجاءً أدخل رقم جوال صحيح.");
      return;
    }

    try {
      setGuestLoading(true);
      setGuestError("");
      localStorage.setItem("guestPhone", normalized);

      const payload = {
        items: [{ part_id: part.id, price: part.price ?? 0 }],
        phone_number: normalized,
      };
      const res = await axios.post(`${API_BASE_URL}/guest-carts`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      const guessed =
        res.data?.cart?.items?.find((ci) => Number(ci.part_id) === Number(part.id)) ??
        res.data?.items?.find?.((ci) => Number(ci.part_id) === Number(part.id)) ??
        null;

      if (guessed?.id) setCartItemId(guessed.id);
      setShowGuestModal(false);
      alert("✅ تمت إضافة القطعة إلى سلة الضيف!");
    } catch {
      setGuestError("لم نتمكن من إنشاء السلة. حاول مرة ثانية.");
    } finally {
      setGuestLoading(false);
    }
  };

  const handleAddToCart = () => {
    const token = localStorage.getItem("token");
    if (!token) return openGuestModal();
    addToCartAuth();
  };

  const handleBuyNow = () => {
    const payload = {
      parts: [
        {
          part,
          brand: brandName,
          model: modelName,
          year: yearValue,
          quantity: qty,
        },
      ],
    };
    onBuyNow?.(payload);
  };

  /* ---------- computed ---------- */
  const compat = useMemo(() => {
    const bits = [brandName, modelName, yearValue, categoryValue].filter(Boolean);
    return bits.length ? bits.join(" / ") : null;
  }, [brandName, modelName, yearValue]);

  const partUrl = `${window.location.origin}/part/${part?.id ?? ""}`;

  return {
    // state
    mainImage, setMainImage,
    qty, setQty,
    cartItemId, checkingCart,
    showGuestModal, guestPhone, setGuestPhone, guestLoading, guestError,

    // actions
    handleAddToCart, handleRemoveFromCart, handleBuyNow,
    openGuestModal, closeGuestModal, confirmGuestAdd,

    // misc
    shareRef, showShareBox, setShowShareBox,
    compat, partUrl,
  };
}
